import React, { useState, useEffect, useRef } from 'react';
import './ServerScriptRunner.css';
import { curDevopsApi, curDevopsToken } from '../_config.js';

const ServerScriptRunner = ({ 
  buttonText = "Run Script", 
  useWebSocket = true, // Set to true to use WebSocket instead of REST API
  showOutputModal = true, // Set to true to show output in a modal
}) => {
  const [loading, setLoading] = useState(false);
  const [scriptName, setScriptName] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [availableScripts, setAvailableScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  
  // WebSocket variables
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [scriptOutput, setScriptOutput] = useState([]);
  const [wsStatus, setWsStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const outputEndRef = useRef(null);

  // Fetch available scripts from the API
  useEffect(() => {
    const fetchAvailableScripts = async () => {
      setLoadingScripts(true);
      try {
        const response = await fetch(`${curDevopsApi}/api/server-scripts/available-scripts`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}` // Assuming you store auth token in localStorage
          }
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch available scripts. Status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (Array.isArray(data)) {
          // If the response is a direct array
          setAvailableScripts(data.map(scriptName => ({
            id: scriptName,
            name: scriptName.replace(/-/g, ' ').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')
          })));
        } else if (data && Array.isArray(data.scripts)) {
          // If the response is an object with a scripts array property
          setAvailableScripts(data.scripts.map(scriptName => ({
            id: scriptName,
            name: scriptName.replace(/-/g, ' ').split(' ').map(word => 
              word.charAt(0).toUpperCase() + word.slice(1)
            ).join(' ')
          })));
        } else {
          setAvailableScripts([]);
          console.error('Unexpected response format:', data);
        }
      } catch (err) {
        console.error('Error fetching available scripts:', err);
        setError(`Failed to load available scripts: ${err.message}`);
      } finally {
        setLoadingScripts(false);
      }
    };

    fetchAvailableScripts();
  }, []);

  // Auto-scroll to bottom when new output is added
  useEffect(() => {
    if (outputEndRef.current) {
      outputEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [scriptOutput]);

  // Clean up WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  const runScriptREST = async () => {
    if (!scriptName) {
      setError("Please select a script to run");
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${curDevopsApi}/api/server-scripts/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${curDevopsToken}` // token is static token in web config
        },
        body: JSON.stringify({
          script_name: scriptName
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to run script');
      }
      
      setResult(data);
      
    } catch (err) {
      setError(err.message || 'An error occurred while running the script');
      console.error('Script execution error:', err);
    } finally {
      setLoading(false);
    }
  };

  const runScriptWebSocket = () => {
    if (!scriptName) {
      setError("Please select a script to run");
      return;
    }

    // Clear previous output and errors
    setScriptOutput([]);
    setError(null);
    setResult(null);
    setLoading(true);
    setWsStatus('connecting');
    
    // Open the modal if enabled
    if (showOutputModal) {
      setIsModalOpen(true);
    }
    
    // Close previous WebSocket if exists
    if (wsRef.current) {
      wsRef.current.close();
    }
    
    // Get token from localStorage
    const token = curDevopsToken;
    
    // Create WebSocket URL
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsBaseUrl = curDevopsApi.replace(/^https?:\/\//, '');
    const wsUrl = `${protocol}//${wsBaseUrl}/api/server-scripts/ws/run-script/${scriptName}`;
    
    // Add token as query parameter if available
    const wsUrlWithToken = token ? `${wsUrl}?token=${curDevopsToken}` : wsUrl;
    
    // Create and configure WebSocket
    const ws = new WebSocket(wsUrlWithToken);
    wsRef.current = ws;
    
    ws.onopen = () => {
      setWsStatus('connected');
      setScriptOutput(prev => [...prev, `Starting ${scriptName}...`]);
    };
    
    ws.onmessage = (event) => {
      setScriptOutput(prev => [...prev, event.data]);
      
      // Check if the message contains a run ID (last message)
      if (event.data.includes('Run ID:')) {
        const runId = event.data.replace('Run ID:', '').trim();
        setResult({ run_id: runId, script_name: scriptName });
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setScriptOutput(prev => [...prev, 'WebSocket error occurred']);
      setWsStatus('error');
      setError('Connection error. Please try again.');
    };
    
    ws.onclose = () => {
      setWsStatus('disconnected');
      setLoading(false);
      // Don't close the modal automatically so user can see the output
    };
  };

  const runScript = () => {
    if (useWebSocket) {
      runScriptWebSocket();
    } else {
      runScriptREST();
    }
  };

  const closeModal = () => {
    if (wsRef.current) {
      wsRef.current.close();
    }
    setIsModalOpen(false);
    setLoading(false);
  };

  // Format script name for display
  const getEnvironmentLabel = (scriptName) => {
    if (scriptName.includes('production')) return 'Production';
    if (scriptName.includes('staging')) return 'Staging';
    if (scriptName.includes('demo')) return 'Demo';
    return 'Unknown';
  };

  // Extract app type from script name
  const getAppTypeLabel = (scriptName) => {
    if (scriptName.includes('admin')) return 'Admin';
    if (scriptName.includes('app')) return 'App';
    if (scriptName.includes('survey')) return 'Survey';
    if (scriptName.includes('pulse')) return 'Pulse';
    if (scriptName.includes('custom-survey')) return 'Custom Survey';
    return '';
  };

  // Get CSS class for script based on environment
  const getScriptClass = (scriptId) => {
    if (scriptId.includes('production')) return 'production-script';
    if (scriptId.includes('staging')) return 'staging-script';
    if (scriptId.includes('demo')) return 'demo-script';
    return '';
  };

  return (
    <div className="script-runner">
      <div className="script-selector">
        <label htmlFor="script-select">Select Build or Deploy Script:</label>
        <select 
          id="script-select"
          value={scriptName}
          onChange={(e) => setScriptName(e.target.value)}
          disabled={loading || loadingScripts}
          className={scriptName ? getScriptClass(scriptName) : ''}
        >
          <option value="">-- Select a script --</option>
          {loadingScripts ? (
            <option value="" disabled>Loading scripts...</option>
          ) : (
            availableScripts.map(script => (
              <option 
                key={script.id} 
                value={script.id}
                className={getScriptClass(script.id)}
              >
                {script.name}
              </option>
            ))
          )}
        </select>
      </div>
      
      <button 
        onClick={runScript} 
        disabled={loading || !scriptName}
        className={`run-script-button ${scriptName && scriptName.includes('production') ? 'production-button' : ''}`}
      >
        {loading ? 'Running...' : buttonText}
      </button>
      
      {error && (
        <div className="script-error">
          Error: {error}
        </div>
      )}
      
      {!isModalOpen && result && !useWebSocket && (
        <div className="script-result">
          <h4>Script Execution Started</h4>
          <p>Run ID: <code>{result.run_id}</code></p>
          <p>You can check the script logs for execution status and details.</p>
        </div>
      )}
      
      {/* WebSocket Output Modal */}
      {isModalOpen && showOutputModal && (
        <div className="script-modal-overlay">
          <div className="script-modal-content">
            <div className="script-modal-header">
              <h3>
                {scriptName && availableScripts.find(s => s.id === scriptName)?.name || 'Script'} Output
              </h3>
              <button className="close-modal-button" onClick={closeModal}>×</button>
            </div>
            
            <div className="script-modal-body">
              <div className="script-status-bar">
                <span className={`script-status ${wsStatus}`}>
                  {wsStatus === 'connected' ? 'Running' : 
                   wsStatus === 'connecting' ? 'Connecting' : 
                   wsStatus === 'error' ? 'Error' : 'Disconnected'}
                </span>
                
                {result && (
                  <span className="script-run-id">
                    Run ID: <code>{result.run_id}</code>
                  </span>
                )}
              </div>
              
              <div className="script-output-container">
                {scriptOutput.map((line, index) => (
                  <div key={index} className="script-output-line">
                    {line}
                  </div>
                ))}
                <div ref={outputEndRef} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerScriptRunner;